import m from 'mithril';
import { Store } from '../store';
import { ApiService } from '../services/api';
import { Button } from '../components/Buttons';
import { t } from '../i18n';
import { currentTheme, setTheme, Theme, FontStyle, currentFontPrimary, currentFontSecondary, setFontPrimary, setFontSecondary } from '../theme';
import { ProductCard } from '../components/ProductCard';

export const ProfilePage: m.Component = {
    async oninit() {
        // If user isn't in store yet, try to restore from localStorage directly
        // (guards against the initApp() async race condition)
        if (!Store.state.user) {
            const userJson = localStorage.getItem('auth_user');
            if (userJson) {
                try { Store.state.user = JSON.parse(userJson); } catch { }
            }
        }
        if (!Store.state.user) {
            m.route.set('/');
            return;
        }
        state.name = Store.state.user.name;
        state.email = Store.state.user.email;
        state.isLoadingOrders = true;
        ApiService.getMyOrders().then(_ => state.orders = _.data || [])
            .finally(() => { state.isLoadingOrders = false; m.redraw() })
    },
    view() {
        if (!Store.state.user) return null;
        const currentTab = m.route.param('tab') || 'profile';
        const user = Store.state.user;
        const initials = (user.name || 'U').split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);

        const navItems = [
            { id: 'profile', icon: 'lucide:user', label: 'Personal Info', color: '#3b82f6' }, // blue
            { id: 'security', icon: 'lucide:shield-check', label: 'Security', color: '#22c55e' }, // green
            { id: 'orders', icon: 'lucide:package', label: 'Order History', color: '#f97316' }, // orange
            { id: 'wishlist', icon: 'lucide:heart', label: 'My Wishlist', color: '#ef4444' }, // red
            { id: 'settings', icon: 'lucide:settings-2', label: 'Appearance', color: '#a855f7' }, // purple
        ];

        return m('div', { class: 'w-full animate-fade-in text-[var(--color-text-base)]' }, [

            // ── Page-level close / back button ────────────────────────────
            m('div', { class: 'flex items-center gap-3 mb-5' }, [
                m('button', {
                    class: '!m-0 !border-0 flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-bold opacity-60 hover:opacity-100 hover:bg-[var(--color-border)]/60 transition-all cursor-pointer',
                    onclick: () => m.route.set('/')
                }, [
                    m('iconify-icon', { icon: 'lucide:arrow-left', class: 'text-base' }),
                    'Back'
                ]),
                m('h1', { class: 'text-xl font-black text-[var(--color-text-base)] opacity-70' }, 'My Account')
            ]),

            // ── Two-column layout ──────────────────────────────────────────
            m('div', { class: 'flex flex-col lg:flex-row gap-6' }, [

                // ── LEFT: Profile Card ─────────────────────────────────────
                m('aside', { class: 'w-full lg:w-64 xl:w-72 shrink-0 flex flex-col gap-4' }, [

                    // Profile card
                    m('div', { class: 'bg-[var(--color-surface)] border border-[var(--color-border)] rounded-2xl p-6 flex flex-col items-center text-center gap-3 shadow-sm' }, [
                        // Avatar
                        m('div', { class: 'relative' }, [
                            m('div', {
                                class: 'w-20 h-20 rounded-2xl flex items-center justify-center text-white text-2xl font-black shadow-lg',
                                style: 'background: linear-gradient(135deg, var(--color-primary), var(--color-secondary))'
                            }, initials),
                            m('div', { class: 'absolute -bottom-1 -end-1 w-5 h-5 rounded-full bg-green-400 border-2 border-[var(--color-surface)]' })
                        ]),
                        m('div', [
                            m('p', { class: 'font-black text-lg leading-tight text-[var(--color-text-base)]' }, user.name),
                            m('p', { class: 'text-sm opacity-50 font-medium' }, user.email),
                        ]),
                        // Role badge
                        m('span', {
                            class: 'px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest',
                            style: 'background: var(--color-primary); color: white; opacity: 0.9'
                        }, user.role === 'admin' ? '👑 Admin' : '🛍️ Customer'),
                    ]),

                    // Navigation menu
                    m('nav', { class: 'bg-[var(--color-surface)] border border-[var(--color-border)] rounded-2xl p-2 flex flex-col gap-1 shadow-sm' }, [
                        navItems.map((item, idx) => {
                            const isActive = currentTab === item.id;
                            return m('button', {
                                key: item.id,
                                class: [
                                    '!m-0 !border-0 w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-left text-sm font-bold cursor-pointer group',
                                    isActive
                                        ? 'bg-[var(--color-primary)] text-white shadow-md'
                                        : 'hover:bg-[var(--color-border)]/50 text-[var(--color-text-base)] opacity-70 hover:opacity-100'
                                ].join(' '),
                                onclick: () => m.route.set('/profile', { tab: item.id })
                            }, [
                                // Icon container
                                m('div', {
                                    class: `w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors duration-200 ${isActive ? 'bg-white/20' : ''}`,
                                    style: !isActive ? `background: ${item.color}15; color: ${item.color};` : ''
                                }, [
                                    m('iconify-icon', {
                                        icon: item.icon,
                                        class: `text-lg ${isActive ? 'text-white drop-shadow-sm' : ''}`
                                    })
                                ]),

                                // Label
                                m('span', { class: 'flex-1 tracking-tight' }, item.label),

                                // Chevron (only on active or hover)
                                m('iconify-icon', {
                                    icon: 'lucide:chevron-right',
                                    class: `text-sm transition-all duration-200 ${isActive ? 'text-white opacity-100 translate-x-0' : 'opacity-0 -translate-x-2 group-hover:opacity-40 group-hover:translate-x-0'}`
                                })
                            ]);
                        })
                    ]),

                    // Quick stat cards (compact, in sidebar)
                    m('div', { class: 'grid grid-cols-2 gap-3' }, [
                        { label: 'Orders', value: state.orders.length.toString(), icon: 'lucide:shopping-bag' },
                        { label: 'Wishlist', value: Store.state.favorites.length.toString(), icon: 'lucide:heart' },
                        { label: 'Points', value: '450', icon: 'lucide:star' },
                        { label: 'Year', value: new Date().getFullYear().toString(), icon: 'lucide:calendar' },
                    ].map(stat =>
                        m('div', {
                            class: 'bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl p-3 flex flex-col gap-2 hover:border-[var(--color-primary)]/40 hover:shadow-md transition-all shadow-sm cursor-default'
                        }, [
                            m('div', {
                                class: 'w-8 h-8 rounded-lg flex items-center justify-center',
                                style: 'background: color-mix(in srgb, var(--color-primary) 12%, transparent)'
                            }, [
                                m('iconify-icon', { icon: stat.icon, class: 'text-[var(--color-primary)] text-base' })
                            ]),
                            m('p', { class: 'font-black text-xl text-[var(--color-text-base)] leading-none mt-1' }, stat.value),
                            m('p', { class: 'text-[10px] font-bold uppercase tracking-widest opacity-40' }, stat.label),
                        ])
                    )),
                ]),
                m('main', { class: 'flex-1 min-w-0' }, [
                    // Content card with gradient header inside
                    m('div', { class: 'bg-[var(--color-surface)] border border-[var(--color-border)] rounded-2xl shadow-sm overflow-hidden min-h-[400px]' }, [
                        // Gradient header — only above right panel
                        m('div', {
                            class: 'px-6 py-4 flex items-center gap-4 border-b border-[var(--color-border)]',
                            style: 'background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)'
                        }, [
                            m('div', {
                                class: 'w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shrink-0'
                            }, [
                                m('iconify-icon', {
                                    icon: navItems.find(n => n.id === currentTab)?.icon || 'lucide:user',
                                    class: 'text-white text-xl'
                                })
                            ]),
                            m('div', { class: 'flex-1' }, [
                                m('p', { class: 'text-base font-black text-white leading-tight' },
                                    navItems.find(n => n.id === currentTab)?.label || 'Dashboard'
                                ),
                                m('p', { class: 'text-white/60 text-xs font-medium mt-0.5' },
                                    currentTab === 'profile' ? 'Manage your personal details' :
                                        currentTab === 'security' ? 'Keep your account secured' :
                                            currentTab === 'orders' ? 'Track your purchases' :
                                                currentTab === 'wishlist' ? 'Your saved favorite items' :
                                                    'Customize your experience'
                                )
                            ])
                        ]),
                        // Tab content
                        m('div', { class: 'p-6 relative overflow-hidden' }, [
                            m('div', { class: 'absolute -top-20 -end-20 w-60 h-60 bg-[var(--color-primary)]/5 rounded-full blur-3xl pointer-events-none' }),
                            m('div', { class: 'relative z-10' }, [
                                currentTab === 'profile' ? renderProfileTab() :
                                    currentTab === 'security' ? renderSecurityTab() :
                                        currentTab === 'orders' ? renderOrdersTab() :
                                            currentTab === 'wishlist' ? renderWishlistTab() :
                                                currentTab === 'settings' ? renderSettingsTab() : null
                            ])
                        ])
                    ])
                ])
            ])
        ]);
    }
};

const state = {
    // Profile
    name: '',
    email: '',
    isSavingProfile: false,

    // Security
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    isSavingSecurity: false,

    // Orders
    orders: [] as any[],
    isLoadingOrders: true
};
// --- Tabs ---

function renderProfileTab() {
    return m('div', { class: 'space-y-6 animate-fade-in-up' }, [
        m('div', [
            m('h2', { class: 'text-xl font-black text-[var(--color-text-base)] tracking-tight' }, 'Personal Information'),
            m('p', { class: 'text-[var(--color-text-base)] opacity-60 text-sm mt-1 font-medium' }, 'Update your basic profile details here.')
        ]),

        m('form', {
            class: 'space-y-6 pt-2',
            onsubmit: async (e: Event) => {
                e.preventDefault();
                state.isSavingProfile = true;
                try {
                    const res = await ApiService.updateProfile({ name: state.name, email: state.email });
                    Store.state.user = res.user;
                    localStorage.setItem('auth_user', JSON.stringify(res.user));
                    Store.addToast('Profile updated successfully', 'success');
                } catch (err: any) {
                    Store.addToast(err.message || 'Update failed', 'error');
                } finally {
                    state.isSavingProfile = false;
                    m.redraw();
                }
            }
        }, [
            // Standardized form card grouping
            m('div', { class: 'bg-[var(--color-bg-base)] rounded-2xl border border-[var(--color-border)] overflow-hidden shadow-sm' }, [
                m('div', { class: 'p-5 sm:p-6 border-b border-[var(--color-border)]' }, [
                    createInputField('Full Name', 'lucide:user', 'text', state.name, (v) => state.name = v)
                ]),
                m('div', { class: 'p-5 sm:p-6' }, [
                    createInputField('Email Address', 'lucide:mail', 'email', state.email, (v) => state.email = v)
                ])
            ]),

            m('div', { class: 'flex justify-end' }, [
                m(Button, { type: 'submit', variant: 'primary', loading: state.isSavingProfile, icon: 'lucide:save' }, 'Save Changes')
            ])
        ])
    ]);
}

function renderSecurityTab() {
    return m('form', {
        class: 'space-y-6 relative z-10 animate-fade-in-up',
        onsubmit: async (e: Event) => {
            e.preventDefault();
            if (state.newPassword !== state.confirmPassword) {
                Store.addToast('New passwords do not match', 'error');
                return;
            }
            state.isSavingSecurity = true;
            try {
                await ApiService.changePassword({ currentPassword: state.currentPassword, newPassword: state.newPassword });
                Store.addToast('Password updated securely', 'success');
                state.currentPassword = '';
                state.newPassword = '';
                state.confirmPassword = '';
            } catch (err: any) {
                Store.addToast(err.message || 'Failed to update password', 'error');
            } finally {
                state.isSavingSecurity = false;
                m.redraw();
            }
        }
    }, [
        m('div', { class: 'mb-6' }, [
            m('h2', { class: 'text-xl font-black text-[var(--color-text-base)] tracking-tight' }, 'Security Settings'),
            m('p', { class: 'text-[var(--color-text-base)] opacity-60 text-sm mt-1 font-medium' }, 'Ensure your account stays secure with a strict password.')
        ]),

        // Standardized form card grouping
        m('div', { class: 'bg-[var(--color-bg-base)] rounded-2xl border border-[var(--color-border)] overflow-hidden shadow-sm' }, [
            m('div', { class: 'p-5 sm:p-6 border-b border-[var(--color-border)] bg-[var(--color-border)]/10' }, [
                createInputField('Current Password', 'lucide:lock', 'password', state.currentPassword, (v) => state.currentPassword = v)
            ]),
            m('div', { class: 'p-5 sm:p-6 border-b border-[var(--color-border)]' }, [
                createInputField('New Password', 'lucide:shield-check', 'password', state.newPassword, (v) => state.newPassword = v, 6)
            ]),
            m('div', { class: 'p-5 sm:p-6' }, [
                createInputField('Confirm New Password', 'lucide:lock-keyhole', 'password', state.confirmPassword, (v) => state.confirmPassword = v, 6)
            ])
        ]),

        m('div', { class: 'flex justify-end' }, [
            m(Button, { type: 'submit', variant: 'primary', loading: state.isSavingSecurity, icon: 'lucide:shield' }, 'Update Password')
        ])
    ]);
}

function renderOrdersTab() {
    return m('div', { class: 'relative z-10 animate-fade-in-up flex flex-col h-full' }, [
        m('div', { class: 'mb-8 shrink-0' }, [
            m('h2', { class: 'text-2xl font-black text-[var(--color-text-base)] flex items-center gap-3' }, [
                'Order Tracking & History',
                m('span', { class: 'px-2 py-1 rounded-md bg-[var(--color-primary)]/10 text-[var(--color-primary)] text-xs font-bold' }, state.orders.length)
            ]),
            m('p', { class: 'text-[var(--color-text-base)] opacity-60 text-sm mt-1' }, 'Monitor the status of your current shipments and review past purchases.')
        ]),

        state.isLoadingOrders ? m('div', { class: 'py-20 flex justify-center text-[var(--color-primary)]' }, m('iconify-icon', { icon: 'lucide:loader-2', class: 'animate-spin text-4xl' }))
            : state.orders.length === 0 ? m('div', { class: 'flex-1 flex flex-col items-center justify-center p-12 lg:p-20 text-center border-2 border-dashed border-[var(--color-border)] rounded-2xl' }, [
                m('div', { class: 'w-20 h-20 rounded-full bg-[var(--color-surface)] shadow-sm flex items-center justify-center mb-6 text-[var(--color-border)]' }, [
                    m('iconify-icon', { icon: 'lucide:package-open', class: 'text-4xl' })
                ]),
                m('h3', { class: 'text-xl font-black text-[var(--color-text-base)]' }, 'No orders yet'),
                m('p', { class: 'text-[var(--color-text-base)] opacity-60 text-sm mt-2 max-w-xs' }, 'When you make purchases, they will appear here. Start shopping to fill this up!'),
                m(Button, { class: 'mt-8', onclick: () => m.route.set('/') }, 'Browse Products')
            ])
                : m('div', { class: 'space-y-6' }, state.orders.map(order => {
                    const isCompleted = order.status === 'completed';
                    const isPending = order.status === 'pending';

                    // Generate dynamic timeline states based on current order status
                    const timelineSteps = [
                        { id: 'pending', label: 'Order Placed', icon: 'lucide:receipt', active: true, done: true },
                        { id: 'processing', label: 'Processing', icon: 'lucide:box', active: !isPending, done: !isPending },
                        { id: 'shipped', label: 'Shipped', icon: 'lucide:truck', active: isCompleted, done: isCompleted },
                        { id: 'delivered', label: 'Delivered', icon: 'lucide:check-circle-2', active: isCompleted, done: isCompleted }
                    ];

                    return m('div', { class: 'group p-5 md:p-8 bg-[var(--color-surface)] border border-[var(--color-border)] hover:border-[var(--color-primary)]/40 rounded-[var(--radius-xl)] shadow-sm hover:shadow-lg transition-all duration-300' }, [

                        // Top Meta Bar
                        m('div', { class: 'flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6' }, [
                            m('div', { class: 'flex items-center gap-4' }, [
                                m('div', {
                                    class: `w-12 h-12 rounded-[var(--radius-lg)] flex items-center justify-center text-xl shadow-inner
                                          ${isCompleted ? 'bg-green-500/10 text-green-600' : 'bg-orange-500/10 text-orange-600'}`
                                }, [
                                    m('iconify-icon', { icon: isCompleted ? 'lucide:package-check' : 'lucide:package-search' })
                                ]),
                                m('div', [
                                    m('div', { class: 'flex items-center gap-2 mb-0.5' }, [
                                        m('p', { class: 'font-black text-lg text-[var(--color-text-base)] leading-none' }, `Order #${order.id}`),
                                        m('span', { class: `px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest ${isCompleted ? 'bg-green-500 text-white' : 'bg-orange-500 text-white'}` }, order.status)
                                    ]),
                                    m('p', { class: 'text-xs font-semibold uppercase tracking-widest opacity-50' }, new Date(order.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }))
                                ])
                            ]),
                            m('div', { class: 'text-left sm:text-right p-3 sm:p-0 bg-[var(--color-border)]/20 sm:bg-transparent rounded-[var(--radius-lg)] sm:rounded-none' }, [
                                m('p', { class: 'text-[10px] font-bold uppercase tracking-widest opacity-60 mb-0.5' }, 'Order Total'),
                                m('p', { class: 'font-black text-2xl text-[var(--color-primary)] leading-none' }, `$${Number(order.total_amount).toFixed(2)}`)
                            ])
                        ]),

                        // Tracking Timeline component
                        m('div', { class: 'pt-6 border-t border-[var(--color-border)] relative' }, [
                            // Base Line
                            m('div', { class: 'absolute top-[45px] inset-inline-6 h-1 bg-[var(--color-border)]/50 rounded-full hidden sm:block' }),
                            // Progress Line
                            m('div', { class: `absolute top-[45px] start-6 h-1 bg-[var(--color-primary)] rounded-full hidden sm:block transition-all duration-700 ease-out inline-start`, style: `width: ${isCompleted ? '100' : isPending ? '10' : '50'}%` }),

                            m('div', { class: 'flex flex-col sm:flex-row justify-between gap-6 sm:gap-0' },
                                timelineSteps.map((step, idx) => m('div', { class: 'flex flex-row sm:flex-col items-center gap-4 sm:gap-2 relative z-10 flex-1 sm:text-center' }, [
                                    // Mobile line connector
                                    idx !== timelineSteps.length - 1 && step.done && m('div', { class: 'absolute top-10 bottom-[-1.5rem] left-[15px] w-0.5 bg-[var(--color-primary)] sm:hidden' }),

                                    m('div', {
                                        class: `w-8 h-8 rounded-full flex items-center justify-center shrink-0 border-2 transition-colors duration-300
                                                ${step.active ? 'bg-[var(--color-primary)] border-[var(--color-primary)] text-white shadow-md' : 'bg-[var(--color-surface)] border-[var(--color-border)] text-[var(--color-text-base)] opacity-40'}`
                                    }, [
                                        m('iconify-icon', { icon: step.icon, class: 'text-sm' })
                                    ]),
                                    m('div', { class: 'flex-1 sm:flex-none' }, [
                                        m('p', { class: `text-xs sm:text-sm font-bold ${step.active ? 'text-[var(--color-text-base)]' : 'opacity-40'}` }, step.label),
                                        step.active && idx === timelineSteps.filter(s => s.active).length - 1 && m('p', { class: 'text-[10px] sm:hidden text-[var(--color-primary)] font-bold mt-0.5' }, 'Current Status')
                                    ])
                                ]))
                            )
                        ])
                    ]);
                }))
    ]);
}

function renderWishlistTab() {
    const favorites = Store.state.favorites;
    
    return m('div', { class: 'relative z-10 animate-fade-in-up flex flex-col h-full' }, [
        m('div', { class: 'mb-8 shrink-0' }, [
            m('h2', { class: 'text-2xl font-black text-[var(--color-text-base)] flex items-center gap-3' }, [
                'My Saved Items',
                m('span', { class: 'px-2 py-1 rounded-md bg-red-500/10 text-red-500 text-xs font-bold' }, favorites.length)
            ]),
            m('p', { class: 'text-[var(--color-text-base)] opacity-60 text-sm mt-1' }, 'Products you\'ve liked. Easily add them to your cart from here.')
        ]),

        favorites.length === 0 
            ? m('div', { class: 'flex-1 flex flex-col items-center justify-center p-12 lg:p-20 text-center border-2 border-dashed border-[var(--color-border)] rounded-2xl' }, [
                m('div', { class: 'w-20 h-20 rounded-full bg-red-50 shadow-sm flex items-center justify-center mb-6 text-red-500' }, [
                    m('iconify-icon', { icon: 'lucide:heart', class: 'text-4xl' })
                ]),
                m('h3', { class: 'text-xl font-black text-[var(--color-text-base)]' }, 'Your wishlist is empty'),
                m('p', { class: 'text-[var(--color-text-base)] opacity-60 text-sm mt-2 max-w-xs mb-8' }, 'Explore our collections and tap the heart icon to save products you love.'),
                m(Button, { onclick: () => m.route.set('/') }, 'Discover Products')
            ])
            : m('div', { class: 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4' }, 
                favorites.map(p => m(ProductCard, { key: p.id, product: p }))
            )
    ]);
}

function renderSettingsTab() {
    const availableThemes: { id: Theme, color: string, name: string }[] = [
        { id: 'day', color: '#f8fafc', name: 'Day' },
        { id: 'night', color: '#0f172a', name: 'Night' },
        { id: 'ocean', color: '#ecfeff', name: 'Ocean' },
        { id: 'forest', color: '#f0fdf4', name: 'Forest' },
        { id: 'sunset', color: '#fff7ed', name: 'Sunset' },
        { id: 'rose', color: '#fff1f2', name: 'Rose' },
        { id: 'purple', color: '#faf5ff', name: 'Purple' },
        { id: 'indigo', color: '#eef2ff', name: 'Indigo' },
        { id: 'dracula', color: '#282a36', name: 'Dracula' },
        { id: 'nord', color: '#2e3440', name: 'Nord' },
        { id: 'monokai', color: '#272822', name: 'Monokai' },
        { id: 'cyberpunk', color: '#fce205', name: 'Cyberpunk' },
        { id: 'hacker', color: '#000000', name: 'Hacker' },
        { id: 'valentine', color: '#e11d48', name: 'Valentine' },
        { id: 'aqua', color: '#083344', name: 'Aqua' },
        { id: 'magma', color: '#1a1a1a', name: 'Magma' },
        { id: 'coffee', color: '#3e2723', name: 'Coffee' },
        { id: 'neon', color: '#000000', name: 'Neon' },
        { id: 'midnightruby', color: '#050505', name: 'M. Ruby' },
        { id: 'matrix', color: '#001100', name: 'Matrix' },
        { id: 'solarflare', color: '#ffffff', name: 'S. Flare' },
        { id: 'abyss', color: '#000b18', name: 'Abyss' },
        { id: 'retrowave', color: '#2a0845', name: 'R. Wave' },
        { id: 'paper', color: '#f4efeb', name: 'Paper' }
    ];

    const availableFonts: { id: FontStyle, label: string, sample: string }[] = [
        // Sans-Serif
        { id: 'sans', label: 'Modern Sans', sample: 'Aa' },
        { id: 'roboto', label: 'Roboto', sample: 'Rr' },
        { id: 'montserrat', label: 'Montserrat', sample: 'Mm' },
        { id: 'poppins', label: 'Poppins', sample: 'Pp' },
        { id: 'manrope', label: 'Manrope', sample: 'Mr' },
        { id: 'spacegrotesk', label: 'Space Grotesk', sample: 'Sg' },
        { id: 'syne', label: 'Syne', sample: 'Sy' },
        { id: 'nunito', label: 'Nunito', sample: 'Nn' },
        { id: 'quicksand', label: 'Quicksand', sample: 'Qq' },
        // Serif & Display
        { id: 'serif', label: 'Classic Serif', sample: 'Aa' },
        { id: 'playfair', label: 'Playfair', sample: 'Pf' },
        { id: 'cinzel', label: 'Cinzel', sample: 'Cz' },
        { id: 'cormorant', label: 'Cormorant', sample: 'Cg' },
        { id: 'lora', label: 'Lora', sample: 'Lr' },
        // Monospace
        { id: 'mono', label: 'Developer Mono', sample: '{;}' },
        { id: 'firacode', label: 'Fira Code', sample: '<>' },
        // Arabic
        { id: 'cairo', label: 'Cairo', sample: 'ع' },
        { id: 'tajawal', label: 'Tajawal', sample: 'ت' },
        { id: 'plex', label: 'IBM Plex', sample: 'أ' },
        { id: 'almarai', label: 'Almarai', sample: 'م' },
        { id: 'changa', label: 'Changa', sample: 'ش' },
        { id: 'notonaskh', label: 'Noto Naskh', sample: 'ن' },
        { id: 'zain', label: 'Zain', sample: 'ز' },
        { id: 'reemkufi', label: 'Reem Kufi', sample: 'ك' },
        { id: 'marhey', label: 'Marhey', sample: 'هـ' },
        { id: 'alexandria', label: 'Alexandria', sample: 'ق' },
        { id: 'readex', label: 'Readex Pro', sample: 'ض' },
        { id: 'lalezar', label: 'Lalezar', sample: 'ط' },
        { id: 'amiri', label: 'Amiri', sample: 'ظ' },
        { id: 'aref', label: 'Aref Ruqaa', sample: 'غ' },
        { id: 'katibeh', label: 'Katibeh', sample: 'ص' }
    ];

    return m('div', { class: 'space-y-12 animate-fade-in-up pb-12' }, [

        // Theme Block
        m('div', { class: 'space-y-4' }, [
            m('div', { class: 'flex items-center gap-2 mb-1' }, [
                m('iconify-icon', { icon: 'lucide:palette', class: 'text-[var(--color-primary)] text-lg' }),
                m('h3', { class: 'text-sm font-black tracking-widest uppercase text-[var(--color-text-base)]' }, 'App Theme'),
            ]),
            m('p', { class: 'text-xs opacity-50 mb-4 font-medium' }, 'Choose a theme to personalize your experience.'),
            m('div', { class: 'grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2' },
                availableThemes.map(t =>
                    m('button', {
                        key: t.id,
                        onclick: () => setTheme(t.id),
                        class: [
                            'hover-spring !m-0 !border-0 flex items-center gap-3 px-3 py-2.5 rounded-xl cursor-pointer transition-all duration-200 text-left border-2',
                            currentTheme === t.id
                                ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/8 shadow-sm'
                                : 'border-[var(--color-border)] hover:border-[var(--color-primary)]/40'
                        ].join(' ')
                    }, [
                        // Color swatch circle
                        m('div', {
                            class: 'w-8 h-8 rounded-full shrink-0 shadow-sm border border-black/10 flex items-center justify-center',
                            style: `background: ${t.color}`
                        }, [
                            currentTheme === t.id && m('iconify-icon', {
                                icon: 'lucide:check',
                                class: `text-sm font-bold ${['day', 'ocean', 'forest', 'sunset', 'rose', 'purple', 'indigo', 'solarflare', 'paper', 'cyberpunk'].includes(t.id) ? 'text-gray-800' : 'text-white'}`
                            })
                        ]),
                        m('div', { class: 'min-w-0 flex-1' }, [
                            m('p', { class: 'font-bold text-xs text-[var(--color-text-base)] truncate capitalize' }, t.name),
                            m('p', { class: `text-[10px] font-bold uppercase tracking-wider ${currentTheme === t.id ? 'text-[var(--color-primary)]' : 'opacity-40'}` },
                                currentTheme === t.id ? '● Active' : 'Preview'
                            )
                        ])
                    ])
                )
            )
        ]),

        // Typography Block
        m('div', { class: 'space-y-8 pt-8 border-t border-[var(--color-border)]' }, [
            m('div', { class: 'flex items-center gap-2 mb-1' }, [
                m('iconify-icon', { icon: 'lucide:type', class: 'text-[var(--color-primary)] text-lg' }),
                m('h3', { class: 'text-sm font-black tracking-widest uppercase text-[var(--color-text-base)]' }, 'Typography Selection'),
            ]),
            m('p', { class: 'text-xs opacity-50 mb-6 font-medium max-w-2xl' }, 'Select a Primary font for your high-impact headings, and a Secondary font for highly legible body text.'),

            // Primary Font (Headings)
            m('div', { class: 'space-y-4 bg-[var(--color-border)]/10 p-4 sm:p-6 rounded-[var(--radius-xl)]' }, [
                m('h4', { class: 'text-xs font-bold tracking-widest text-[var(--color-primary)] uppercase flex items-center gap-2' }, [
                    m('iconify-icon', { icon: 'lucide:heading-1' }), 'Primary Font (Headings)'
                ]),
                m('div', { class: 'grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3' },
                    availableFonts.map(f =>
                        m('button', {
                            onclick: () => setFontPrimary(f.id),
                            class: `hover-spring w-full p-3 rounded-lg cursor-pointer transition-all duration-300 border-2 text-center flex flex-col items-center justify-center min-h-[80px]
                                    ${currentFontPrimary === f.id ? 'bg-[var(--color-primary)]/10 border-[var(--color-primary)] text-[var(--color-primary)] shadow-md' : 'bg-[var(--color-surface)] border-[var(--color-border)] text-[var(--color-text-base)] hover:border-[var(--color-primary)]/50 hover:shadow-sm'}`
                        }, [
                            m('span', { class: 'block font-black text-xl mb-1' }, f.sample),
                            m('span', { class: 'font-bold text-[10px] tracking-tight truncate w-full px-1' }, f.label)
                        ])
                    )
                )
            ]),

            // Secondary Font (Body)
            m('div', { class: 'space-y-4 bg-[var(--color-border)]/10 p-4 sm:p-6 rounded-[var(--radius-xl)]' }, [
                m('h4', { class: 'text-xs font-bold tracking-widest text-[var(--color-secondary)] uppercase flex items-center gap-2' }, [
                    m('iconify-icon', { icon: 'lucide:align-left' }), 'Secondary Font (Body Text)'
                ]),
                m('div', { class: 'grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3' },
                    availableFonts.map(f =>
                        m('button', {
                            onclick: () => setFontSecondary(f.id),
                            class: `hover-spring w-full p-3 rounded-lg cursor-pointer transition-all duration-300 border-2 text-center flex flex-col items-center justify-center min-h-[80px]
                                    ${currentFontSecondary === f.id ? 'bg-[var(--color-secondary)]/10 border-[var(--color-secondary)] text-[var(--color-secondary)] shadow-md' : 'bg-[var(--color-surface)] border-[var(--color-border)] text-[var(--color-text-base)] hover:border-[var(--color-secondary)]/50 hover:shadow-sm'}`
                        }, [
                            m('span', { class: 'block font-medium text-xl mb-1' }, f.sample),
                            m('span', { class: 'font-bold text-[10px] tracking-tight truncate w-full px-1' }, f.label)
                        ])
                    )
                )
            ])
        ])
    ]);
}

// --- Helpers ---

function createInputField(label: string, icon: string, type: string, value: string, setValue: (v: string) => void, minlength?: number) {
    return m('div', { class: 'space-y-1.5' }, [
        m('label', { class: 'text-xs font-black opacity-60 tracking-widest uppercase ms-1 text-[var(--color-text-base)]' }, label),
        m('div', { class: 'relative group' }, [
            m('iconify-icon', {
                icon,
                class: 'absolute start-4 top-1/2 -translate-y-1/2 text-[var(--color-primary)] opacity-50 group-focus-within:opacity-100 transition-all pointer-events-none text-base'
            }),
            m('input', {
                type, required: true, minlength,
                // Use an explicit rgba overlay for the background to guarantee contrast on all dark themes
                class: 'w-full ps-12 pe-4 py-3.5 rounded-xl outline-none font-medium transition-all',
                style: `background: rgba(128, 128, 128, 0.1); color: var(--color-text-base); border: 2px solid var(--color-border);`,
                onfocus: (e: any) => {
                    e.target.style.borderColor = 'var(--color-primary)';
                    e.target.style.boxShadow = '0 0 0 3px color-mix(in srgb, var(--color-primary) 15%, transparent)';
                },
                onblur: (e: any) => {
                    e.target.style.borderColor = 'var(--color-border)';
                    e.target.style.boxShadow = 'none';
                },
                value,
                oninput: (e: any) => setValue(e.target.value)
            })
        ])
    ]);
}
